import './Settings.css';

import {useQueryClient} from '@tanstack/react-query';
import {useEffect, useState} from 'react';

import {apiService} from '../../apiService';
import {useAuth} from '../../contexts/AuthContext';
import {
  employeeProfileQueryKey,
  useEmployeeProfile,
} from '../../hooks/useEmployeeProfile';

const VALIDATION_PATTERNS = {
  PHONE: /^\d{10}$/,
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  NAME: /^[a-zA-Z\s-']+$/,
};

const NewSettings = () => {
  const {user, updateUser} = useAuth();
  const queryClient = useQueryClient();
  const {data: profile} = useEmployeeProfile();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editMode, setEditMode] = useState(false);

  const [userInfo, setUserInfo] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
  });

  const [originalUserInfo, setOriginalUserInfo] = useState({});

  const [validationErrors, setValidationErrors] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
  });

  useEffect(() => {
    if (editMode) return;
    const source = profile || user;
    if (source) {
      const formattedUserData = {
        first_name: source.first_name || '',
        last_name: source.last_name || '',
        phone_number: source.phone_number || '',
        email: source.email || '',
      };

      setUserInfo(formattedUserData);
      setOriginalUserInfo(formattedUserData);
      setLoading(false);
    }
  }, [profile, user, editMode]);

  const validateField = (fieldName, value) => {
    if (!editMode) return '';

    let errorMessage = '';

    // Check for empty required fields
    if (
      (fieldName === 'first_name' ||
        fieldName === 'last_name' ||
        fieldName === 'phone_number' ||
        fieldName === 'email') &&
      !value.trim()
    ) {
      return `${fieldName.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} is required`;
    }

    switch (fieldName) {
      case 'first_name':
      case 'last_name':
        if (value && !VALIDATION_PATTERNS.NAME.test(value)) {
          errorMessage =
            'Name should only contain letters, spaces, hyphens, and apostrophes';
        }
        break;
      case 'phone_number':
        if (value && !VALIDATION_PATTERNS.PHONE.test(value)) {
          errorMessage = 'Phone number must be exactly 10 digits';
        }
        break;
      case 'email':
        if (value && !VALIDATION_PATTERNS.EMAIL.test(value)) {
          errorMessage = 'Please enter a valid email address';
        }
        break;
      default:
        break;
    }

    return errorMessage;
  };

  const handleInputChange = (e) => {
    if (!editMode) return;

    const {id, value} = e.target;

    setUserInfo({...userInfo, [id]: value});

    // Validate the field
    const errorMessage = validateField(id, value);
    setValidationErrors({
      ...validationErrors,
      [id]: errorMessage,
    });
  };

  // Handle phone number input
  const handlePhoneChange = (e) => {
    if (!editMode) return;

    // Only allow numeric input
    const value = e.target.value.replace(/\D/g, '');

    setUserInfo({...userInfo, phone_number: value});

    const errorMessage = validateField('phone_number', value);
    setValidationErrors({
      ...validationErrors,
      phone_number: errorMessage,
    });
  };

  // Validate all fields
  const validateAllFields = () => {
    if (!editMode) return true;

    const errors = {
      first_name: validateField('first_name', userInfo.first_name),
      last_name: validateField('last_name', userInfo.last_name),
      phone_number: validateField('phone_number', userInfo.phone_number),
      email: validateField('email', userInfo.email),
    };

    setValidationErrors(errors);

    return !Object.values(errors).some((error) => error !== '');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editMode) {
      setEditMode(true);
      return;
    }

    if (!user?.uid) {
      setError('User ID not found. Please log in again.');
      return;
    }

    // Validate all fields before submission
    if (!validateAllFields()) {
      setError('Please correct the validation errors before saving.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const requestBody = {
        uid: user.uid,
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
        phone_number: userInfo.phone_number,
        email: userInfo.email,
      };

      const token = localStorage.getItem('token');
      apiService.setAuthToken(token);

      const data = await apiService.put(
        '/api/update-employee-info/',
        requestBody,
      );

      if (data) {
        setSuccessMessage('Settings updated successfully!');
        setEditMode(false);
        setOriginalUserInfo({...userInfo});

        if (updateUser) {
          updateUser({
            ...user,
            first_name: userInfo.first_name,
            last_name: userInfo.last_name,
            phone_number: userInfo.phone_number,
            email: userInfo.email,
          });
        }

        queryClient.invalidateQueries({
          queryKey: employeeProfileQueryKey(user?.uid),
        });

        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      }
    } catch (err) {
      console.error('Error updating employee info:', err);
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditMode(false);
    setUserInfo({...originalUserInfo});
    setValidationErrors({
      first_name: '',
      last_name: '',
      phone_number: '',
      email: '',
    });
    setError('');
    setSuccessMessage('');
  };

  const formatPhoneForDisplay = (phone) => {
    if (!phone || phone.length !== 10) return phone;
    return `(${phone.substring(0, 3)}) ${phone.substring(3, 6)}-${phone.substring(6)}`;
  };

  if (loading) {
    return <div className='settings-loading'>Loading user information...</div>;
  }

  if (!user) {
    return <div className='settings-loading'>Loading user information...</div>;
  }

  return (
    <div className='settings-content'>
      {error && <div className='settings-error'>{error}</div>}
      {successMessage && (
        <div className='settings-success'>{successMessage}</div>
      )}

      <form onSubmit={handleSubmit}>
        <section className='settings-section'>
          <h2>Personal Information</h2>

          <div className='form-group'>
            <label htmlFor='first_name'>First Name *</label>
            {editMode ? (
              <input
                type='text'
                id='first_name'
                className={`form-control ${validationErrors.first_name ? 'invalid' : ''}`}
                placeholder='Enter your first name'
                value={userInfo.first_name}
                onChange={handleInputChange}
                required
              />
            ) : (
              <div className='form-control readonly'>
                {userInfo.first_name || 'Not set'}
              </div>
            )}
            {validationErrors.first_name && (
              <div className='validation-error'>
                {validationErrors.first_name}
              </div>
            )}
          </div>

          <div className='form-group'>
            <label htmlFor='last_name'>Last Name *</label>
            {editMode ? (
              <input
                type='text'
                id='last_name'
                className={`form-control ${validationErrors.last_name ? 'invalid' : ''}`}
                placeholder='Enter your last name'
                value={userInfo.last_name}
                onChange={handleInputChange}
                required
              />
            ) : (
              <div className='form-control readonly'>
                {userInfo.last_name || 'Not set'}
              </div>
            )}
            {validationErrors.last_name && (
              <div className='validation-error'>
                {validationErrors.last_name}
              </div>
            )}
          </div>

          <div className='form-group'>
            <label htmlFor='phone_number'>Phone Number (10 digits) *</label>
            {editMode ? (
              <input
                type='tel'
                id='phone_number'
                className={`form-control ${validationErrors.phone_number ? 'invalid' : ''}`}
                placeholder='Enter your phone number (digits only)'
                value={userInfo.phone_number}
                onChange={handlePhoneChange}
                maxLength={10}
                required
              />
            ) : (
              <div className='form-control readonly'>
                {formatPhoneForDisplay(userInfo.phone_number) || 'Not set'}
              </div>
            )}
            {validationErrors.phone_number && (
              <div className='validation-error'>
                {validationErrors.phone_number}
              </div>
            )}
            {editMode && <div className='form-note'>Enter 10 digits</div>}
          </div>

          <div className='form-group'>
            <label htmlFor='email'>Email *</label>
            {editMode ? (
              <input
                type='email'
                id='email'
                className={`form-control ${validationErrors.email ? 'invalid' : ''}`}
                placeholder='Enter your email address'
                value={userInfo.email}
                onChange={handleInputChange}
                required
              />
            ) : (
              <div className='form-control readonly'>
                {userInfo.email || 'Not set'}
              </div>
            )}
            {validationErrors.email && (
              <div className='validation-error'>{validationErrors.email}</div>
            )}
          </div>
          <div className='form-note'>* Required fields</div>
        </section>

        {/* <section className="settings-section">
          <h2>Organization</h2>
          
          <div className="form-group">
            <label>Organization ID</label>
            <div className="form-control readonly">
              {user.company_code || 'Not set'}
            </div>
          </div>
        </section>
         */}
        <div className='form-actions'>
          {editMode && (
            <button
              type='button'
              className='cancel-button'
              onClick={handleCancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
          )}
          <button type='submit' className='save-button' disabled={saving}>
            {!editMode
              ? 'Edit Settings'
              : saving
                ? 'Saving...'
                : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewSettings;
